// src/components/ToastProvider.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Toast from './Toast';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: number;
    message: string;
    type: ToastType;
    duration: number;
}

interface ToastContextType {
    addToast: (message: string, type?: ToastType, duration?: number) => void;
    removeToast: (id: number) => void;
    showSuccess: (message: string, duration?: number) => void;
    showError: (message: string, duration?: number) => void;
    showWarning: (message: string, duration?: number) => void;
    showInfo: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((message: string, type: ToastType = 'info', duration: number = 5000): void => {
        const id = Date.now() + Math.random();
        
        // Aynı mesaj zaten varsa yeni toast ekleme
        setToasts(prev => {
            const existingToast = prev.find(toast => 
                toast.message === message && toast.type === type
            );
            
            if (existingToast) {
                return prev;
            }
            
            return [...prev, { id, message, type, duration }];
        });
    }, []);

    const removeToast = useCallback((id: number): void => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const showSuccess = useCallback((message: string, duration?: number): void => {
        addToast(message, 'success', duration);
    }, [addToast]);

    const showError = useCallback((message: string, duration?: number): void => {
        addToast(message, 'error', duration);
    }, [addToast]);

    const showWarning = useCallback((message: string, duration?: number): void => {
        addToast(message, 'warning', duration);
    }, [addToast]);

    const showInfo = useCallback((message: string, duration?: number): void => {
        addToast(message, 'info', duration);
    }, [addToast]);

    const value: ToastContextType = {
        addToast,
        removeToast,
        showSuccess,
        showError,
        showWarning,
        showInfo
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        duration={toast.duration}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

